import { useState, useEffect } from 'react';
import { getNewAccessToken } from './ProtectedRoute/ProtectedRoute.jsx';
import { POSTrequestOptions } from './constants.jsx';
import { MenuFoldOutlined, MenuUnfoldOutlined, CloseOutlined } from '@ant-design/icons';
import { Modal } from "antd";


export async function fetchWithAccessTokenRetry(url, options) {
    const response = await fetch(url, options);
    if (response.status === 401) {
        // If unauthorized, try to get a new access token
        await getNewAccessToken();
        // Retry the request with the new token
        return fetch(url, options);
    }
    return response;
}

export function renderOptions(options) {
  return options.map(option => <option key={option} value={option}>{option}</option>);
}

export function renderFormRow(field, item, handleInputChange, editable, handleInputBlur) {
    const { key, descriptor, inputType, options } = field;

    return (
        <div className="form-row" key={key}>
            <label htmlFor={key + "_" + item.id}>{descriptor}:</label>
            {options ? (
                <select disabled={!editable} readOnly={!editable} onChange={(event) => handleInputChange(event, item, key, field)} id={key + "_" + item.id} value={item[key] || ''} className="form-input">
                    {renderOptions(options, item[key])}
                </select>
            ) :
                <input disabled={!editable} id={key + "_" + item.id} className="form-input" type={inputType} value={item[key] === null ? '' : item[key]} 
                    onChange={(event) => handleInputChange(event, item, key, field)} 
                    onBlur={handleInputBlur ? (event) => handleInputBlur(event, item, key, field) : null}
                />
            }
        </div>
    );
}

export async function genericGETDatabaseCall(endpoint, name) {
    let requestOptions = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    };

    const response = await fetchWithAccessTokenRetry(endpoint, requestOptions);
    const responseData = await response.json();

    if (!response.ok) {
        throw new Error('Failed to get ' + name + ': ' + responseData.error);
    }
    return responseData; // Return the fetched data
}

export async function genericPOSTDatabaseCall(endpoint, data, name) {
    let requestOptions = {
        ...POSTrequestOptions,
        body: JSON.stringify(data),
    };

    const response = await fetchWithAccessTokenRetry(endpoint, requestOptions);
    const responseData = await response.json();

    if (!response.ok) {
        throw new Error('Failed to add ' + name + ': ' + responseData.error);
    }

    return responseData; // Return the added data
}

export async function genericDELETEDatabaseCall(endpoint, data, name) {
    let requestOptions = {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    };

    const response = await fetchWithAccessTokenRetry(endpoint, requestOptions);
    const responseData = await response.json();

    if (!response.ok) {
        throw new Error('Failed to delete ' + name + ': ' + responseData.error);
    }

    return responseData; // Return the deleted data
}

export function ShowAndHide({ children, title, defaultOpen = false }) {
  const [isVisible, setIsVisible] = useState(defaultOpen);

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  return (
    <div className="show-hide-container">
      <h2 className="section-title">
        {
          isVisible ? (<MenuFoldOutlined className="icon" onClick={toggleVisibility} />) : (<MenuUnfoldOutlined className="icon" onClick={toggleVisibility} />)
        }
        {title}</h2>
      {isVisible && <div className="content">{children}</div>}
      <hr className="divider" />
    </div>
  );
}

export function SidebarButton({ icon, itemInModal, name, onClickUpdateCalc, isModal = true }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const showModal = () => {
        setIsModalOpen(true);
    };
    const handleOk = () => {
        setIsModalOpen(false);
    };
    const handleCancel = () => {
        setIsModalOpen(false);
    };

    return (
        <>
            <button title={`Edit ${name}`} type="button" className='sidebar-button' onClick={isModal ? showModal : onClickUpdateCalc}>
                {icon}
            </button>
            {isModal && (
                <Modal title={`Edit ${name}`} open={isModalOpen} onOk={handleOk} onCancel={handleCancel} width={"70%"} style={{top: 20}}>
                    <CloseOutlined className='modal-close-icon' onClick={handleCancel} />
                    {itemInModal}
                </Modal>
            )}
        </>
    );
}

export function RenderOneTable({ data, name, styles, tableStyles }) {
    if (data.length === 0 ) return <div>No data available</div>;
    
    return (
        <div style={styles !== undefined ? styles : { overflowX: 'auto', marginBottom: '20px'}}>
            <table style={tableStyles} className="entity-table">
                <thead>
                <tr>
                    <th>{name}</th>
                    <th>Value</th>
                </tr>
                </thead>
                <tbody>
                        {data[0].map((item, index) => (
                            <tr>
                                <td key={index}>{item}</td>
                                <td key={data[1][index]}>{data[1][index]}</td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    );
}

export function RenderProjectionTable({ data, name, styles, tableStyles }) {
    if (data.length === 0 ) return <div>No data available</div>;

    return (
        <div style={styles !== undefined ? styles : { overflowX: 'auto', marginBottom: '20px'}}>
            <table style={tableStyles} className="entity-table">
                <thead>
                <tr>
                    <th>{name}</th>
                    {data[1].slice(1).map((item, index) => (
                        <th key={index}>Year {index}</th>
                    ))}
                </tr>
                </thead>
                <tbody>
                    {data.map((item, index) => (
                        <tr key={index}>
                            {item.map((value, subIndex) => (
                                <td key={subIndex}>{value}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


export function RenderMultiSelectTable({ data , isYearsRelated = true}) {
    const [index, setIndex] = useState(1);
    const [selectedArray, setSelectedArray] = useState(data[index]);

    useEffect(() => {
        setSelectedArray(data[index]);
    }, [data, index]);

    return (
        <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
            <table className="entity-table">
                <thead>
                <tr>
                    <th className="has-select">
                        <select style={{ cursor: 'pointer' }} className="entity-table-select" onChange={(e) => {
                            const selectedIndex = e.target.value;
                            setSelectedArray(data[selectedIndex]);
                            setIndex(selectedIndex);
                        }}>
                            {data[0].map((option, index) => (
                                <option key={index} value={index + 1}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </th>
                    {isYearsRelated && selectedArray[0].slice(1).map((item, index) => (
                        <th key={index}>Year {index}</th>
                    ))}
                </tr>
                </thead>
                <tbody>
                    {selectedArray.map((item, index) => (
                        <tr key={index}>
                            {isYearsRelated && item.map((value, subIndex) => (
                                <td key={subIndex}>{value}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
